// UniversalEditableTable.jsx

import { useMemo, useState, useEffect } from 'react'
import { useTable, useRowSelect, usePagination } from 'react-table'
import { styled } from '@mui/material/styles'
import { Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from '@mui/material'
import { ArrowUpward, ArrowDownward, DeleteForever, AddCircle } from '@mui/icons-material'

const bgColor = 'rgba(0, 0, 0, 0)'//'#121212',

const StyledTable = styled(Table)({
  borderCollapse: 'separate',
  borderSpacing: '0 2px',
  '& th': {
    backgroundColor: '#F5F5F5',
    fontWeight: 'bold',
    padding: '2px',
  },
  '& td': {
    backgroundColor: bgColor,
    borderBottom: '1px solid #666',
    padding: '2px',
  },
})

const UniversalEditableTable = ({colNames, theData, updateData, height='300px'}) => {
  const [tableData, setTableData] = useState(theData)
  const [currentPage, setCurrentPage] = useState(0)

  let data = tableData  

  useEffect(()=>{
    setTableData(theData)
  }, [theData])

  const columns = useMemo(
    () => [
      {
        Header: colNames[0],
        accessor: colNames[0],
        Cell: ({ row, column, value, onChange }) => (
          <TextField value={value} onChange={(e) => onChange(e.target.value)} sx={{gap: 0, p: 0, border: 'none', "& .MuiOutlinedInput-input": {padding: 0}, "& fieldset": { border: 'none' }}} />
        ),
      },
      {
        Header: colNames[1],
        accessor: colNames[1],
        Cell: ({ row, column, value, onChange }) => (
          <TextField value={value} onChange={(e) => onChange(e.target.value)} sx={{gap: 0, p: 0, border: 'none', "& .MuiOutlinedInput-input": {p: 0, ml: '20px'}, "& fieldset": { border: 'none' }}} />
        ),
      },
    ],
    [/*colNames*/]
  )



  const tableInstance = useTable({ columns, data, initialState: { pageIndex: currentPage, pageSize: 10 }}, usePagination, useRowSelect, (hooks) => {
    hooks.allColumns.push((columns) => [
      {
        id: 'selection',
        //disableResizing: true,
        //minWidth: 35,
        //maxWidth: 0,
        //maxWidth: 35,
        /*Header: ({ getToggleAllRowsSelectedProps }) => (
          <Checkbox {...getToggleAllRowsSelectedProps()} />
        ),*/
        Cell: ({ row, column, value }) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IconButton size="small" color="primary" aria-label="move row up" component="label" style={{padding: '1px'}} onTouchEnd={() => handleDelete(row.index)} onClick={() => handleDelete(row.index)}>
              <DeleteForever fontSize="small"/>
            </IconButton>
            <IconButton size="small" color="primary" aria-label="move row up" component="label" style={{padding: '1px'}} onTouchEnd={() => handleRowMoveUp(row)} onClick={() => handleRowMoveUp(row)} disabled={row.index === 0}>
              <ArrowUpward fontSize="small"/>
            </IconButton>
            <IconButton size="small" color="primary" aria-label="move row up" component="label" style={{padding: '1px'}} onTouchEnd={() => handleRowMoveDown(row)} onClick={() => handleRowMoveDown(row)} >
              <ArrowDownward fontSize="small"/>
            </IconButton>
            <IconButton size="small" color="primary" aria-label="move row up" component="label" style={{padding: '1px'}} onTouchEnd={() => handleRowInsert(row)} onClick={() => handleRowInsert(row)}>
              <AddCircle fontSize="small"/>
            </IconButton>
          </div>
        ),
      },
      ...columns,
    ])
  })

  const { 
    getTableProps, 
    getTableBodyProps, 
    headerGroups, 
    //rows, 
    prepareRow, 
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    //setPageSize,
    state: { 
      pageIndex, 
      //pageSize 
    }
  } = tableInstance

  const handleCellChange = (/*rowIndex,*/ columnId, value, trueIndex) => {
    setTableData((prevData) =>
      prevData.map((row, i) => {
        if (trueIndex === i) {
          return { ...row, [columnId]: value }
        } else {
          return row
        }
      })
    )
    
    //setCurrentPage(Math.floor(trueIndex/10))
  }

  const handleRowMoveDown = (inRow) => {
    let rowIndex = inRow.index
    
    setTableData((prevData) =>{
      if(inRow.index >= prevData.length-1){
        return prevData
      }
      let thisRow = prevData[inRow.index]
      let nextRow = prevData[inRow.index+1]
      return prevData.map((row, i) => {
        if (i === rowIndex) {
          return nextRow
        } else if (i === rowIndex + 1){
          return thisRow
        }else {
          return row
        }
      })
    })
    
    setCurrentPage(Math.floor(inRow.index/10))
  }

  const handleRowMoveUp = (inRow) => {
    let rowIndex = inRow.index
    
    setTableData((prevData) =>{
      if(inRow.index <= 0){
        return prevData
      }
      let thisRow = prevData[inRow.index]
      let nextRow = prevData[inRow.index-1]
      return prevData.map((row, i) => {
        if (i === rowIndex) {
          return nextRow
        } else if (i === rowIndex - 1){
          return thisRow
        } else {
          return row
        }
      })
    })

    setCurrentPage(Math.floor(inRow.index/10))
  }

  const handleRowInsert = (inRow) => {
    let rowIndex = inRow.index

    setTableData(prevData => [...prevData, {[colNames[0]]:'', [colNames[1]]:''}])
    setTableData((prevData) =>{
      if(inRow.index < 0){
        return prevData
      }
      let thisRow = prevData[inRow.index]
      return prevData.map((row, i) => {
        if (i === rowIndex) {
          return thisRow
        } else if (i === rowIndex + 1){
          return {[colNames[0]]:'', [colNames[1]]:''}
        } else if (i > rowIndex + 1){
          return prevData[i-1]
        } else {
          return row
        }
      })
    })

    setCurrentPage(Math.floor(inRow.index/10))
  }

  const handleDelete = (idx) => {
    // If this is the first entry,
    // return without deleting.
    if(idx===0){
      return
    }
    setTableData((prevData)=>{
      return prevData.filter((row, index) => index !==idx)
    })
    
    setCurrentPage(Math.floor(idx/10))
  }

  return (
    <>
    <TableContainer style={{maxHeight:height}}>
      <StyledTable defaultpagesize={10} {...getTableProps()} >
        <TableHead style={{position:'sticky', top:'0', zIndex:'100'}}>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()} >
              {headerGroup.headers.map((column, i) => (
                <TableCell key={i} {...column.getHeaderProps()} style={{padding:'6px 20px 6px 20px', fontWeight: '500', lineHeight: '1rem', color: 'white', backgroundColor: '#1976d2', backgroundImage: 'linear-gradient(180deg,#888,#777)'/*backgroundImage: 'linear-gradient(180deg,#223399,#2240bb)'*/}}>
                  {column.render('Header')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, i) => {
            prepareRow(row)
            return (
              <TableRow {...row.getRowProps()}>
                {row.cells.map((cell, j) => {
                  return (
                    <TableCell key={j} {...cell.getCellProps()} style={{padding:'0px'}} >
                      {cell.render('Cell', {
                        row,
                        j,
                        value: tableData[i+10*pageIndex][cell.column.id],
                        onChange: (e) => {handleCellChange(/*i,*/ cell.column.id, e, i+10*pageIndex)},
                      })}
                    </TableCell>
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </StyledTable>
    </TableContainer>
    <Button variant='contained' fullWidth onClick={()=>{updateData(tableData)}} style={{borderRadius: '0', /*color:'#fff',*/ height: '30px', padding:'6px', backgroundImage: 'linear-gradient(180deg,#888,#777)'}}>Update</Button>
    <div className="pagination">
      <div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
        <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {"<<"}
        </Button>
        <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {"<"}
        </Button>
        <Button onClick={() => nextPage()} disabled={!canNextPage}>
          {">"}
        </Button>
        <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {">>"}
        </Button>
      </div>
      <div style={{width: '100%', display: 'flex', justifyContent: 'space-between'}}>
        <span>
          Page
          <strong>
          {" "}{pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>
        <span>
          Go to page:{" "}
          <input
            type="number"
            value={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
            style={{ width: "30px" }}
          />
        </span>
      </div>
      {/*<select
        value={pageSize}
        onChange={(e) => {
          setPageSize(Number(e.target.value));
        }}
      >
        [10, 20, 30, 40, 50].map((pageSize) => (
          <option key={pageSize} value={pageSize}>
            Show {pageSize}
          </option>
        ))
      </select>*/}
    </div>
  </>
  )
}

export default UniversalEditableTable
